// Het fasevenster in Taken (Rens, 26/10/2026): een fase uitwerken zoals een taak. Links de omschrijving, de checklist,
// het verloop en de bijlagen; rechts de fase zelf (datums uit de taken, labels, bucket, fase erboven), wacht op, de
// taken eronder, en Uren · Clockify met het Clockify-project van de fase. Dat project geldt voor alles eronder: nieuwe
// Clockify-taken van de taken in deze fase en haar subfases komen erin (de dichtstbijzijnde fase erboven wint, anders
// het klantplan). Bestaande Clockify-taken blijven waar ze staan. Een fase heeft geen eigenaar: de planner wijzigt,
// een medewerker leest en plaatst updates. Dezelfde onderdelen als het taakvenster (components/task/).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { clockifyApi, fmtHours, useClockifyLive, useClockifyWrite, type ClockifyLive, type FaseProject } from "../lib/api-clockify";
import { catOf, labelColor } from "../lib/board";
import { count } from "../lib/cleanup";
import { faseAsTask, faseChain, faseProjectOf, tasksByFase } from "../lib/fases";
import { useIndex, useMe, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { deadline, dmy, plannerTaskUrl } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import type { Fase, PlanningData, Task, TaskOp } from "../lib/types";
import { Av } from "../components/Avatar";
import { clockifyReady } from "../components/TaskCard";
import { TaskHistory } from "../components/TaskHistory";
import { AttachmentsSection } from "../components/task/Attachments";
import { ChecklistSection } from "../components/task/Checklist";
import { DepsSection } from "../components/task/Deps";
import { LabelsSection } from "../components/task/Labels";
import type { TaskPerm } from "../components/task/perm";
import { useToast } from "../shell/Toast";

/** Het venster over de lijst: de hele fase; sluiten met Escape, het kruisje of een klik ernaast. */
export function FaseModal({ fase, data, onClose }: { fase: Fase; data: PlanningData; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  useEffect(() => {
    const h = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName)) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <>
      <button type="button" className="p-scrim" aria-label="Venster sluiten" onClick={onClose} />
      <div ref={box} className="p-tk-modal" role="dialog" aria-modal="true" aria-label={"Fase " + fase.title} tabIndex={-1}>
        <FasePage key={fase.id} fase={fase} data={data} onClose={onClose} />
      </div>
    </>
  );
}

function FasePage({ fase, data, onClose }: { fase: Fase; data: PlanningData; onClose: () => void }) {
  const { data: me } = useMe();
  const rights = rightsOf(useSettings().data?.settings);
  const idx = useIndex(data);
  const ops = useTaskOps();
  const [, setParams] = useSearchParams();
  const { data: liveData } = useClockifyLive();
  const [title, setTitle] = useState<string | null>(null);
  const [desc, setDesc] = useState<string | null>(null);
  const [parentOpen, setParentOpen] = useState(false);
  const task = useMemo(() => faseAsTask(fase), [fase]);
  // de fases van de klant waar deze fase onder kan: niet zichzelf en niets onder haar
  const parentOpts = useMemo(() => {
    const own = data.fases.filter((x) => x.projectId === fase.projectId && x.progress < 1), ids = new Set(own.map((x) => x.id));
    const out: { f: Fase; lvl: number }[] = [];
    const walk = (parent: string | null, lvl: number, seen: Set<string>) => {
      for (const f of own.filter((x) => (parent ? x.parentId === parent : !x.parentId || !ids.has(x.parentId))).sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))) {
        if (seen.has(f.id) || f.id === fase.id) continue;
        out.push({ f, lvl });
        walk(f.id, lvl + 1, new Set([...seen, f.id]));
      }
    };
    walk(null, 0, new Set());
    return out;
  }, [data.fases, fase.id, fase.projectId]);
  if (!me) return null;

  const med = me.role === "medewerker", write = me.canWrite, plan = !med && write;
  const perm: TaskPerm = {
    me, med, own: false, canEdit: !med, canPick: false, readOnly: med, write, edit: plan, plan, inPlanner: !med && !write,
    medDirect: false, finishOther: false, direct: false, proposeEnd: false, proposal: null, url: plannerTaskUrl(me, fase), rights,
  };
  const klant = idx.klanten.get(fase.projectId)?.name || "", bucket = idx.buckets.get(fase.bucketId)?.name || "";
  const above = faseChain(fase.parentId, idx.fases);
  const under = (tasksByFase(data.tasks, idx.fases).get(fase.id) || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const d = deadline(fase.end, data.today);
  const live = liveData && clockifyReady(liveData.clockify) ? liveData : null;
  const run = (o: TaskOp[], message: string) => { ops.run(task, o, message).catch(() => {}); };
  const open = (key: "taak" | "fase", id: string) => setParams((p) => { p.delete("taak"); p.delete("fase"); p.set(key, id); return p; });
  const saveTitle = () => { const v = (title || "").trim(); setTitle(null); if (v && v !== fase.title) run([{ kind: "title", text: v }], "Titel gewijzigd: " + v); };
  const saveDesc = () => { const v = (desc || "").trim(); setDesc(null); if (v !== (fase.notes || "")) run([{ kind: "notes", text: v }], "Omschrijving opgeslagen · zichtbaar in Planner binnen 1 à 2 minuten"); };
  const onKey = (e: KeyboardEvent, ok: () => void, cancel: () => void) => { if (e.key === "Enter") { e.preventDefault(); ok(); } if (e.key === "Escape") { e.preventDefault(); cancel(); } };
  const moveParent = (v: string) => {
    setParentOpen(false);
    const f = v ? idx.fases.get(v) : null;
    if ((f?.id || null) === (fase.parentId || null)) return;
    run([{ kind: "parent", faseId: f ? f.id : null }], f ? fase.title + " staat nu onder " + f.title : fase.title + " staat niet meer onder een fase");
  };
  // een taak in een subfase: de lagen tussen deze fase en de taak
  const below = (t: Task) => { const c = faseChain(t.faseId, idx.fases); const i = c.findIndex((f) => f.id === fase.id); return c.slice(i + 1).map((f) => f.title).join(" › "); };
  const stand = (t: Task) => { const c = catOf(t); return c === "actief" ? deadline(t.end, data.today) : { text: c === "geparkeerd" ? "geparkeerd" : "af te sluiten", tone: "" as const }; };

  return (
    <div className="p-tk-page">
      <div className="p-tk-dh">
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="ex-eyebrow" style={{ margin: 0 }}>{klant}{bucket ? " · " + bucket : ""}</p>
          {above.length > 0 && (
            <div className="p-dw-pad"><Icon name="fase" size={13} />
              {above.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Deze fase openen" onClick={() => open("fase", f.id)}>{f.title}</button></Fragment>)}
            </div>
          )}
          <div className="p-tk-title">
            {title != null
              ? <><input className="ex-input" value={title} maxLength={250} autoFocus aria-label="Titel" onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => onKey(e, saveTitle, () => setTitle(null))} />
                <button type="button" className="ex-btn ex-btn--primary" onClick={saveTitle}>Bewaren</button><button type="button" className="ex-btn" onClick={() => setTitle(null)}>Annuleren</button></>
              : <><h1><span className="p-fk-chip" style={{ verticalAlign: "middle", marginRight: 10 }}><Icon name="fase" size={11} />Fase</span>{fase.title}</h1>{plan && <button type="button" className="p-tk-pen" title="Titel wijzigen" aria-label="Titel wijzigen" onClick={() => setTitle(fase.title)}><Icon name="pen" size={16} /></button>}</>}
          </div>
          <div className="p-tk-pills">
            <span className={"ex-pill ex-pill--" + (under.length ? d.tone || "off" : "off")}><span className="ex-dot" />{d.long}</span>
            <span className="p-fk-prog" title={fase.done + " van " + fase.total + " taken af"} style={{ fontSize: 13 }}><i><b style={{ width: (fase.total ? Math.round((fase.done / fase.total) * 100) : 0) + "%" }} /></i>{fase.done} van {fase.total} af</span>
            {fase.labels.map((l) => <span key={l.id} className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span>)}
            {fase.pending && <span className="p-meta-i t-mute">Planner verwerkt een wijziging</span>}
          </div>
        </div>
        <div className="p-tk-acts">
          <Link className="ex-btn" to={"/planning?fase=" + fase.id} title="De fase op het bord, in de faselade"><Icon name="board" size={15} />Op het bord</Link>
          <a className="ex-btn" href={perm.url} target="_blank" rel="noreferrer" title="Deze fase openen in Planner"><Icon name="ext" size={15} />Planner</a>
          <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten (Esc)" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
      </div>

      {med && (
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
          <p>Een fase wijzigt de planner. U kunt ze bekijken, de taken erin openen en een update plaatsen.</p>
        </div>
      )}
      {!med && !write && (
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
          <p>Wijzigen doet u in Planner; vanuit de app schrijven kan enkel met een Project-licentie. Een update plaatsen en het Clockify-project zetten kan wel.</p>
        </div>
      )}

      <div className="p-tk-grid">
        <div className="p-tk-col">
          <section className="p-tk-box" id="fz-desc">
            <div className="p-tk-boxh"><h3>Omschrijving</h3>
              {plan && desc == null ? <button type="button" className="ex-link p-icl" onClick={() => setDesc(fase.notes || "")}><Icon name="pen" size={13} />{fase.notes ? "Wijzigen" : "Schrijven"}</button> : <span className="p-kd-note" style={{ margin: 0 }}>de notitie in Planner</span>}
            </div>
            {desc != null
              ? <>
                <textarea className="ex-input p-tk-ta" value={desc} autoFocus aria-label="Omschrijving" placeholder="Waar gaat deze fase over, wat is het doel, wat moet een collega weten?" onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setDesc(null); }} />
                <div className="p-quick" style={{ alignItems: "center" }}><button type="button" className="ex-btn ex-btn--primary" onClick={saveDesc}>Opslaan</button><button type="button" className="ex-btn" onClick={() => setDesc(null)}>Annuleren</button><span className="p-kd-note" style={{ margin: 0 }}>Gaat naar Planner, als tekst met regeleinden</span></div>
              </>
              : <p className={"p-tk-desc" + (fase.notes ? "" : " empty")}>{fase.notes || "Nog geen omschrijving."}</p>}
          </section>

          {((fase.checklist || []).length > 0 || plan) && <section className="p-tk-box"><ChecklistSection task={task} perm={perm} run={run} page /></section>}

          <section className="p-tk-box">
            <div className="p-tk-boxh"><h3>Verloop</h3><span className="p-kd-note" style={{ margin: 0 }}>updates en wijzigingen, het nieuwste onderaan</span></div>
            <div className="p-tk-vl"><TaskHistory task={task} data={data} canLabel={plan} write={write} owners="de planner" plannerUrl={perm.url} /></div>
          </section>

          <section className="p-tk-box"><AttachmentsSection task={task} perm={perm} run={run} page /></section>
        </div>

        <div className="p-tk-col">
          <section className="p-tk-box">
            <h3>Fase</h3>
            <div className="p-tk-kv">
              <span className="k">Start</span><span className="v"><b>{dmy(fase.start)}</b><span className="p-kd-note" style={{ margin: 0 }}>uit de taken</span></span>
              <span className="k">Deadline</span><span className="v"><b>{dmy(fase.end)}</b><span className="p-kd-note" style={{ margin: 0 }}>de laatste deadline eronder</span></span>
              <span className="k">Labels</span><span className="v"><LabelsSection task={task} data={data} perm={perm} run={run} always /></span>
              <span className="k">Bucket</span><span className="v">{plan && !fase.parentId
                ? <select className="ex-input p-tk-sel" aria-label="Bucket" value={fase.bucketId} onChange={(e) => run([{ kind: "bucket", bucketId: e.target.value }], "Bucket " + (idx.buckets.get(e.target.value)?.name || "") + ": " + fase.title)}>
                  {data.buckets.filter((b) => b.projectId === fase.projectId).sort((a, b) => a.name.localeCompare(b.name)).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                : <><b>{bucket || "–"}</b>{fase.parentId && <span className="p-kd-note" style={{ margin: 0 }}>die van de fase erboven</span>}</>}</span>
              <span className="k">Fase erboven</span><span className="v">{parentOpen
                ? <><select className="ex-input p-tk-sel" aria-label="Fase erboven" autoFocus value={fase.parentId || ""} style={{ maxWidth: "100%" }} onChange={(e) => moveParent(e.target.value)}>
                    <option value="">Geen fase</option>
                    {parentOpts.map(({ f, lvl }) => <option key={f.id} value={f.id}>{"— ".repeat(Math.min(lvl, 4))}{f.title}</option>)}
                  </select><button type="button" className="ex-btn-xs" onClick={() => setParentOpen(false)}>Annuleren</button></>
                : <>{above.length
                  ? <span className="p-dw-pad" style={{ margin: 0 }}><Icon name="fase" size={13} />{above.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Deze fase openen" onClick={() => open("fase", f.id)}>{f.title}</button></Fragment>)}</span>
                  : <span className="p-kd-note" style={{ margin: 0 }}>bovenaan in het plan</span>}
                  {plan && <button type="button" className="ex-link p-icl" onClick={() => setParentOpen(true)}>{above.length ? "Verplaatsen" : "Onder een fase zetten"}</button>}</>}</span>
            </div>
          </section>

          <section className="p-tk-box"><DepsSection task={task} data={data} perm={perm} ops={ops} page /></section>

          <section className="p-tk-box">
            <div className="p-tk-boxh"><h3>Taken in deze fase</h3><span className="p-kd-note" style={{ margin: 0 }}>{under.length ? count(under.length) + " open" : "niets meer open"}</span></div>
            {under.length > 0 && (
              <div className="p-fz-list">
                {under.map((t) => {
                  const s = stand(t), b2 = below(t);
                  return (
                    <button key={t.id} type="button" className="p-fz-row" onClick={() => open("taak", t.id)}>
                      <span className="t"><b>{t.title}</b>{b2 && <small>{b2}</small>}</span>
                      <span className="p-tk-avs">{t.owners.length ? t.owners.map((o) => <Av key={o.personId} id={o.personId} init={idx.people.get(o.personId)?.initials || "?"} cls="p-av s" />) : <span className="p-av s none" title="Zonder eigenaar"><Icon name="userx" size={12} /></span>}</span>
                      <span className={"p-dl " + (s.tone ? "t-" + s.tone : "t-mute")} style={{ fontSize: 12.5 }}>{s.text}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {!under.length && <p className="p-kd-note" style={{ margin: 0 }}>Alle taken in deze fase zijn afgewerkt, of er staan nog geen taken in.</p>}
          </section>

          <section className="p-tk-box" id="fz-uren">
            <h3>Uren · Clockify</h3>
            {live ? <FaseHours fase={fase} data={data} under={under} live={live} planner={!med} /> : <p className="p-kd-note" style={{ margin: 0 }}>Clockify is niet gekoppeld of nog niet gelezen.</p>}
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * De uren van de taken in de fase samen, en het Clockify-project van de fase: de planner kiest het uit de projecten in
 * Clockify (gegroepeerd per klant) of haalt het weg. Zonder eigen project geldt dat van een fase erboven, anders dat
 * van het klantplan.
 */
function FaseHours({ fase, data, under, live, planner }: { fase: Fase; data: PlanningData; under: Task[]; live: ClockifyLive; planner: boolean }) {
  const idx = useIndex(data);
  const qc = useQueryClient();
  const cw = useClockifyWrite();
  const toast = useToast();
  const [pick, setPick] = useState<string | null>(null);
  const { data: cl } = useQuery({ queryKey: ["clockify-klanten"], queryFn: clockifyApi.clients, staleTime: 5 * 60e3, enabled: planner });
  const own: FaseProject | undefined = live.faseProjects?.[fase.id];
  const inherited = own ? null : faseProjectOf(fase.parentId, idx.fases, live.faseProjects);
  const plan = live.plans[fase.projectId];
  const effective = own || inherited?.project || (plan ? { clockifyProjectId: plan.clockifyProjectId, name: plan.name, client: plan.client } : null);
  const hrs = under.map((t) => live.tasks[t.id]).filter((h) => h && !h.none && !h.missing);
  const logged = hrs.reduce((a, h) => a + (h.logged || 0), 0), est = hrs.filter((h) => h.estimate != null), estSum = est.reduce((a, h) => a + (h.estimate || 0), 0);
  const inThis = effective ? hrs.filter((h) => h.projectId === effective.clockifyProjectId).length : 0, other = hrs.length - inThis, none = under.length - hrs.length;
  const setLink = (pid: string | null, msg: string, prev: string | null) => {
    cw.run(() => clockifyApi.setFaseLink(fase.id, pid)).then((r) => {
      qc.setQueryData<ClockifyLive>(["clockify-live"], (old) => (old ? { ...old, faseProjects: r.byFase } : old));
      setPick(null);
      toast.flash(r.text || msg, { undo: () => { cw.run(() => clockifyApi.setFaseLink(fase.id, prev)).then((r2) => qc.setQueryData<ClockifyLive>(["clockify-live"], (old) => (old ? { ...old, faseProjects: r2.byFase } : old))).catch(() => {}); } });
    }).catch(() => {});
  };
  const projectName = (pid: string) => { for (const c of cl?.clients || []) { const p = c.projects.find((x) => x.id === pid); if (p) return (c.name ? c.name + " · " : "") + p.name; } return "dit project"; };
  return (
    <>
      <div className="p-ckm">
        <div className="p-ckm-top">
          <div className="p-ckm-big"><b>{fmtHours(logged)}</b><span>gelogd op {count(hrs.length).replace("taak", "taak").replace("taken", "taken")} met een Clockify-taak</span></div>
          {estSum > 0 ? <span className="p-ckm-est">van <b>{fmtHours(estSum)}</b> geschat{est.length < hrs.length ? " (" + (hrs.length - est.length) + " zonder schatting)" : ""}</span> : <span className="p-ckm-est">Geen schattingen</span>}
        </div>
        {estSum > 0 && <div className="p-ckm-bar"><i style={{ width: Math.min(100, (logged / estSum) * 100).toFixed(1) + "%" }} /></div>}
      </div>
      <div className="p-fz-proj">
        <div className="p-tk-boxh"><h3>Clockify-project van de fase</h3>{own && planner && <button type="button" className="ex-link p-icl" disabled={cw.busy} onClick={() => setLink(null, "Clockify-project van de fase weggehaald", own.clockifyProjectId)}><Icon name="x" size={13} />Weghalen</button>}</div>
        {effective
          ? <div className="p-fz-cur"><b>{effective.client ? effective.client + " · " : ""}{effective.name}</b><small>{own ? "eigen koppeling van deze fase" : inherited ? "via de fase " + inherited.fase.title : "het project van het klantplan"}</small></div>
          : <p className="p-kd-note" style={{ margin: 0 }}>Nog geen project: het klantplan hangt niet aan een Clockify-project. Koppel het hier, of het klantplan in Koppelingen.</p>}
        <p className="p-kd-note" style={{ margin: 0 }}>Geldt voor alle taken en subfases eronder: nieuwe Clockify-taken en de keuzelijst Bestaande koppelen gebruiken dit project. Bestaande Clockify-taken blijven waar ze staan.</p>
        {under.length > 0 && effective && (
          <p className="p-kd-note" style={{ margin: 0 }}>{count(inThis)} met een Clockify-taak in dit project{other ? " · " + other + " in een ander project" : ""}{none ? " · " + none + " zonder Clockify-taak" : ""}.</p>
        )}
        {planner && (
          pick == null
            ? <div className="p-quick"><button type="button" className="p-tog" onClick={() => setPick(own?.clockifyProjectId || "")}><Icon name="link" size={13} />{own ? "Ander project" : "Project van deze fase kiezen"}</button></div>
            : <div className="p-quick" style={{ alignItems: "center" }}>
              <select className="ex-input p-tk-sel" aria-label="Clockify-project" value={pick} style={{ maxWidth: "100%" }} onChange={(e) => setPick(e.target.value)}>
                <option value="">Kies een project in Clockify</option>
                {(cl?.clients || []).map((c) => <optgroup key={c.id || c.name || "zonder"} label={c.name || "Zonder klant"}>{c.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>)}
              </select>
              <button type="button" className="ex-btn ex-btn--primary" disabled={!pick || cw.busy || pick === own?.clockifyProjectId} onClick={() => setLink(pick, "Clockify-project van " + fase.title + ": " + projectName(pick), own?.clockifyProjectId || null)}>Koppelen</button>
              <button type="button" className="ex-btn" onClick={() => setPick(null)}>Annuleren</button>
            </div>
        )}
      </div>
    </>
  );
}
